'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Bell, Check } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { getUnreadAlertCount, getUnreadMessageCount } from '@/lib/demoData';
import { getFamilySettings, updateFamilySettings, NotificationSettings } from '@/lib/familySettings';

interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}

function Toggle({ checked, onChange, ariaLabel }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-lucat-green focus:ring-offset-2 flex-shrink-0`}
      style={{ width: 48, height: 28, backgroundColor: checked ? '#4CBF1A' : '#D1D5DB' }}
    >
      <span
        className="inline-block bg-white rounded-full shadow transition-transform"
        style={{
          width: 22,
          height: 22,
          transform: checked ? 'translateX(22px)' : 'translateX(3px)',
        }}
      />
    </button>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}

function ToggleRow({ label, description, checked, onChange, ariaLabel }: ToggleRowProps) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div className="flex-shrink-0 pt-0.5">
        <Toggle checked={checked} onChange={onChange} ariaLabel={ariaLabel} />
      </div>
    </div>
  );
}

export default function NotificationsClient() {
  const router = useRouter();
  const unreadAlerts = getUnreadAlertCount();
  const unreadMessages = getUnreadMessageCount();

  const [settings, setSettings] = useState<NotificationSettings>(() => getFamilySettings().notifications);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSettings(getFamilySettings().notifications);
  }, []);

  const update = (key: keyof NotificationSettings, value: boolean) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      return next;
    });
  };

  const handleSave = () => {
    updateFamilySettings({ notifications: settings });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      {/* Sub-header */}
      <div className="bg-lucat-navy px-4 pt-4 pb-5">
        <button
          onClick={() => router.push('/settings')}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
          style={{ minHeight: 44 }}
          aria-label="Retour aux paramètres"
        >
          <ArrowLeft size={18} />
          Retour
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Bell size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Notifications</h1>
            <p className="text-sm text-white/70 mt-0.5">Choisissez les alertes que vous souhaitez recevoir</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-5 space-y-4 pb-8">
        {/* Alertes principales */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-1">Alertes et événements</h2>
          <ToggleRow
            label="Demandes de rappel"
            description="Être prévenue lorsqu'André souhaite être appelé"
            checked={settings.callbackRequests}
            onChange={v => update('callbackRequests', v)}
            ariaLabel="Recevoir les notifications de demandes de rappel"
          />
          <ToggleRow
            label="Appels manqués"
            description="Être prévenue lorsqu'un appel avec André n'a pas abouti"
            checked={settings.missedCalls}
            onChange={v => update('missedCalls', v)}
            ariaLabel="Recevoir les notifications d'appels manqués"
          />
          <ToggleRow
            label="Messages reçus"
            description="Être prévenue lorsqu'André envoie un message"
            checked={settings.receivedMessages}
            onChange={v => update('receivedMessages', v)}
            ariaLabel="Recevoir les notifications de messages reçus"
          />
          <ToggleRow
            label="Photos consultées"
            description="Être prévenue lorsqu'André regarde une photo"
            checked={settings.viewedPhotos}
            onChange={v => update('viewedPhotos', v)}
            ariaLabel="Recevoir les notifications de photos consultées"
          />
          <ToggleRow
            label="Connexion de la tablette"
            description="Être prévenue lorsque la tablette se déconnecte ou se reconnecte"
            checked={settings.tabletConnection}
            onChange={v => update('tabletConnection', v)}
            ariaLabel="Recevoir les notifications de connexion de la tablette"
          />
          <ToggleRow
            label="Rappels d'agenda"
            description="Recevoir un rappel avant une visite ou un événement"
            checked={settings.calendarReminders}
            onChange={v => update('calendarReminders', v)}
            ariaLabel="Recevoir les rappels d'agenda"
          />
        </div>

        {/* Mode de notification */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-1">Mode de notification</h2>
          <ToggleRow
            label="Notifications dans l'application"
            description="Afficher les alertes directement dans LUCAT CONNECT"
            checked={settings.inApp}
            onChange={v => update('inApp', v)}
            ariaLabel="Activer les notifications dans l'application"
          />
          <ToggleRow
            label="Notification sonore"
            description="Émettre un son lors de la réception d'une alerte"
            checked={settings.sound}
            onChange={v => update('sound', v)}
            ariaLabel="Activer les notifications sonores"
          />
          <ToggleRow
            label="E-mail récapitulatif"
            description="Recevoir un résumé quotidien par e-mail"
            checked={settings.emailSummary}
            onChange={v => update('emailSummary', v)}
            ariaLabel="Activer l'e-mail récapitulatif"
          />
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          className="w-full flex items-center justify-center gap-2 bg-lucat-green text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-green/90 active:scale-95 transition-all"
          style={{ minHeight: 52 }}
          aria-label="Enregistrer mes préférences de notifications"
        >
          {saved ? (
            <>
              <Check size={18} />
              Vos préférences ont été enregistrées
            </>
          ) : (
            'Enregistrer mes préférences'
          )}
        </button>
      </div>
    </AppLayout>
  );
}
